import { type BudgetManagerConfig, type OperationType } from '@ownyou/llm-client';
import type {
  AnalyzerLLMClient,
  IkigaiLLMOperation,
  IkigaiRouteLocalOutcome,
  IkigaiRouteLocalOutcomeResult,
  IkigaiRouteLocalOutcomeStatus,
} from '@ownyou/ikigai';
import {
  LlmRouteExecutionError,
  type LlmRouteExecutionErrorCode,
  type LlmRouteModelCatalogEntry,
  type LlmRouteProviderFactory,
  type LlmRouteSelectionSource,
  type LlmRouteTelemetryEvent,
} from './executor';
import { MODEL_ROUTE_IDS, type ModelRouteId } from './routes';
import type { IkigaiRoutePlan } from './default-selector';
import {
  RouteLlmClientAdapterBase,
  routeMetadata,
  type UnavailableRouteInput,
} from './route-llm-adapter-core';

export interface IkigaiRouteLocalOutcomeEvent {
  type: 'ikigai_route_local_outcome';
  routeId: ModelRouteId;
  operation: IkigaiLLMOperation;
  provider: string | null;
  model: string | null;
  selectionSource: LlmRouteSelectionSource | null;
  selectionReason: string | null;
  routePolicyVersion: string | null;
  promptVersion: string | null;
  outputSchemaVersion: string | null;
  privacyClass: string | null;
  status: IkigaiRouteLocalOutcomeStatus;
  result: IkigaiRouteLocalOutcomeResult;
  errorCode?: LlmRouteExecutionErrorCode | 'parse_failed';
}

export interface IkigaiRouteLlmClientAdapterOptions {
  routePlan?: IkigaiRoutePlan | null;
  authToken?: string | null;
  userId: string;
  budgetConfig?: BudgetManagerConfig;
  modelCatalog?: readonly LlmRouteModelCatalogEntry[];
  providerFactory?: LlmRouteProviderFactory;
  emitTelemetry?: (event: LlmRouteTelemetryEvent) => void;
  emitRouteLocalOutcome?: (event: IkigaiRouteLocalOutcomeEvent) => void;
}

export const IKIGAI_OPERATION_ROUTE_IDS: Readonly<Record<IkigaiLLMOperation, ModelRouteId>> = {
  sourceSummary: MODEL_ROUTE_IDS.ikigai.sourceSummary,
  entitySynthesis: MODEL_ROUTE_IDS.ikigai.entitySynthesis,
  selfAudit: MODEL_ROUTE_IDS.ikigai.selfAudit,
  entityComposition: MODEL_ROUTE_IDS.ikigai.entityComposition,
  profileSummary: MODEL_ROUTE_IDS.ikigai.profileSummary,
};

function routeOperation(operation: IkigaiLLMOperation): OperationType {
  return operation === 'profileSummary' ? 'character_summary' : 'ikigai_inference';
}

export class IkigaiRouteLlmClientAdapter
  extends RouteLlmClientAdapterBase<IkigaiRoutePlan, IkigaiLLMOperation>
  implements AnalyzerLLMClient
{
  constructor(private readonly options: IkigaiRouteLlmClientAdapterOptions) {
    super({
      ...options,
      agentLabel: 'ikigai-context',
      routeFamilyLabel: 'Ikigai',
    });
  }

  async complete(
    prompt: string,
    request?: {
      model?: string;
      operation?: IkigaiLLMOperation;
      timeout?: number;
      maxTokens?: number;
    }
  ): Promise<{
    text: string;
    usage?: { inputTokens?: number; outputTokens?: number };
  }> {
    const operation = request?.operation;
    if (!operation) {
      throw new LlmRouteExecutionError(
        'missing_route_selection',
        'Ikigai package operation is required for route execution',
        {
          routeId: MODEL_ROUTE_IDS.ikigai.sourceSummary,
        }
      );
    }

    const routeId = IKIGAI_OPERATION_ROUTE_IDS[operation];
    const result = await this.executeRouteText({
      routeId,
      routeOperation: operation,
      messages: [{ role: 'user', content: prompt }],
      maxTokens: request?.maxTokens,
      userId: this.options.userId,
      executorOperation: routeOperation(operation),
    });

    return {
      text: result.text,
      usage: result.usage,
    };
  }

  emitRouteLocalOutcome(params: {
    operation: IkigaiLLMOperation;
    status: IkigaiRouteLocalOutcomeStatus;
    result: IkigaiRouteLocalOutcomeResult;
    errorCode?: IkigaiRouteLocalOutcomeEvent['errorCode'];
  }): void {
    const routeId = IKIGAI_OPERATION_ROUTE_IDS[params.operation];
    const resolved = this.options.routePlan?.routes[routeId];
    this.options.emitRouteLocalOutcome?.({
      type: 'ikigai_route_local_outcome',
      routeId,
      operation: params.operation,
      provider: resolved?.provider ?? null,
      model: resolved?.model ?? null,
      selectionSource: resolved?.selectionSource ?? null,
      selectionReason: resolved?.reason ?? null,
      ...routeMetadata(routeId),
      status: params.status,
      result: params.result,
      errorCode: params.errorCode,
    });
  }

  recordRouteLocalOutcome(outcome: IkigaiRouteLocalOutcome): void {
    this.emitRouteLocalOutcome({
      operation: outcome.operation,
      status: outcome.status,
      result: outcome.result,
      errorCode: outcome.errorCode as IkigaiRouteLocalOutcomeEvent['errorCode'],
    });
  }

  protected override recordUnavailableOutcome({
    routeOperation,
    code,
  }: UnavailableRouteInput<IkigaiLLMOperation>): void {
    this.emitRouteLocalOutcome({
      operation: routeOperation,
      status: 'not_run',
      result: 'surfaced_failure',
      errorCode: code,
    });
  }
}

export function createIkigaiRouteLlmClientAdapter(
  options: IkigaiRouteLlmClientAdapterOptions
): IkigaiRouteLlmClientAdapter {
  return new IkigaiRouteLlmClientAdapter(options);
}
